/* eslint-disable jira/integration/test-id-by-folder-structure */
import React, { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import type { BoardType } from '@atlassian/jira-boards-common/src/model/index.tsx';
import { KANBAN, SCRUM } from '@atlassian/jira-common-directory-v2/src/model/constants.tsx';
import { useIsFullscreen } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/fullscreen/index.tsx';
import { PROJECT_TITLE } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-container.tsx';
import {
	BoardActions,
	type BoardWithDataId,
} from '@atlassian/jira-navigation-board-action-menu/src/ui/index.tsx';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import type {
	JiraBoardType,
	HorizontalNavBoardHeader_board$key,
} from '@atlassian/jira-relay/src/__generated__/HorizontalNavBoardHeader_board.graphql';
import type { HorizontalNavBoardHeader_project$key } from '@atlassian/jira-relay/src/__generated__/HorizontalNavBoardHeader_project.graphql';
import BaseHeader from '@atlassian/jira-horizontal-nav-header/src/common/ui/BaseHeader.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ExtraComponentsFar } from '../extra-components-far/index.tsx';
import { HorizontalNavProjectBreadcrumbs } from '../breadcrumbs/index.tsx';
import { TeamButton } from '../team-button/index.tsx';

type BoardProps = {
	boardFragment: HorizontalNavBoardHeader_board$key;
	projectFragment?: HorizontalNavBoardHeader_project$key | null;
};

export const HorizontalNavBoardHeader = ({ boardFragment, projectFragment = null }: BoardProps) => {
	const [isFullscreen] = useIsFullscreen();
	const board = useFragment(
		graphql`
			fragment HorizontalNavBoardHeader_board on JiraBoard {
				__typename
				__id @required(action: THROW)
				boardId @required(action: THROW)
				name @required(action: THROW)
				canEdit
				boardType @required(action: THROW)
				favouriteValue {
					isFavourite @required(action: THROW)
				}
			}
		`,
		boardFragment,
	);

	const project = useFragment(
		graphql`
			fragment HorizontalNavBoardHeader_project on JiraProject
			@argumentDefinitions(
				showTeamButton: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-team-button.relayprovider"
				}
			) {
				projectId @include(if: $showTeamButton)
				...ui_navigationBoardActionMenu_BoardActions @skip(if: $isTMPBoardRoute)
				...extraComponentsFar_horizontalNav
				...breadcrumbs_horizontalNav_HorizontalNavProjectBreadcrumbs
			}
		`,
		projectFragment,
	);

	const toBoardType = (jiraBoardType: JiraBoardType) => {
		if (jiraBoardType === 'SCRUM') {
			return SCRUM;
		}

		if (jiraBoardType === 'KANBAN') {
			return KANBAN;
		}
	};

	const boardData: BoardWithDataId | null = useMemo(() => {
		if (board?.__typename !== 'JiraBoard') {
			return null;
		}
		const isPublicBoard = !!board.name;
		return isPublicBoard
			? {
					id: board?.boardId?.toString(),
					dataId: board?.__id,
					admins: {
						users: [],
						groups: [],
					},
					name: board.name,
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					type: toBoardType(board.boardType) as BoardType,
					favourite: board.favouriteValue?.isFavourite ?? false,
					canEdit: !!board.canEdit,
				}
			: {
					id: board?.boardId?.toString(),
					dataId: board?.__id,
					admins: {
						users: [],
						groups: [],
					},
					canEdit: !!board.canEdit,
					isPrivate: true,
				};
	}, [board]);

	const analyticsData = useMemo(
		() => ({
			navigationContainer: PROJECT_TITLE,
		}),
		[],
	);

	if (board == null || board.__typename !== 'JiraBoard' || board.boardId == null) {
		return null;
	}

	return (
		<BaseHeader
			title={board.name}
			isLoading={board == null}
			extraComponentsClose={[
				project?.projectId && fg('team-connection-visibility-in-jira') && (
					<TeamButton key="TeamButton" projectId={project.projectId} isMultiBoard />
				),
				<ContextualAnalyticsData key="BoardActions" attributes={analyticsData}>
					<BoardActions
						key="BoardActions"
						board={boardData}
						projectFragment={project}
						isBoardFavorite={board.favouriteValue?.isFavourite ?? undefined}
					/>
				</ContextualAnalyticsData>,
			]}
			extraComponentsFar={<ExtraComponentsFar projectFragment={project} />}
			testId="horizontal-nav-header.ui.board-header.header"
			extraComponentsAbove={
				!isFullscreen && (
					<HorizontalNavProjectBreadcrumbs projectFragment={project} shouldShowProjectName />
				)
			}
		/>
	);
};
